<template>
  <MainLayout>
    <div class="header">
      <Flex justify="between" align="center" class="mobile-col" :gap="3">
        <Flex :gap="3" justify="start" align="center">
          <div>
            <button
              ref="hamburger"
              class="hamburger"
              @click="menuOpen = !menuOpen">
              <span class="material-icons">menu</span>
            </button>
          </div>
          <Logo :width="72" :height="72" />
          <div>
            <Flex direction="column" :gap="1" class="phone-info">
              <span class="md-typescale-body-large bold">
                {{ t('general.phone_number') }}
              </span>
              <span class="md-typescale-body-large">
                {{ auth.user.value?.phoneNumber || '&nbsp;' }}
              </span>
            </Flex>
          </div>
        </Flex>
        <Flex justify="end" class="desktop-only">
          <Button
            :label="t('general.logout')"
            :disabled="auth.logoutLoading.value"
            @click="auth.logout()">
            <template #icon>
              <LogoutIcon />
            </template>
          </Button>
        </Flex>
      </Flex>
    </div>
    <div class="layout-container">
      <div v-if="menuOpen" class="overlay" @click="menuOpen = false"></div>
      <nav ref="sidebar" :class="['sidebar', { open: menuOpen }]" @click.stop>
        <router-link
          :class="['nav-item', { active: isActive('/devices').value }]"
          to="/devices"
          @click="menuOpen = false">
          <span class="material-icons">devices</span>
          {{ t('nav.devices') }}
        </router-link>
        <router-link
          v-if="auth.user.value?.emergencyContact"
          :class="[
            'nav-item',
            { active: isActive('/emergency-contacts').value },
          ]"
          to="/emergency-contacts"
          @click="menuOpen = false">
          <span class="material-icons">contact_phone</span>
          {{ t('nav.emergency_contacts') }}
        </router-link>
        <router-link
          :class="['nav-item', { active: isActive('/settings').value }]"
          to="/settings"
          @click="menuOpen = false">
          <span class="material-icons">settings</span>
          {{ t('nav.settings') }}
        </router-link>
        <div class="mobile-only logout-wrapper">
          <Button
            :label="t('general.logout')"
            :disabled="auth.logoutLoading.value"
            @click="
              auth.logout();
              menuOpen = false;
            ">
            <template #icon>
              <LogoutIcon />
            </template>
          </Button>
        </div>
      </nav>
      <main class="content">
        <template v-if="auth.user.value">
          <slot></slot>
        </template>
      </main>
    </div>
  </MainLayout>
</template>

<script lang="ts" setup>
import { useAuth } from '@/composables/useAuth';
import MainLayout from '@/layout/MainLayout/MainLayout.vue';
import Flex from '@/components/Flex/Flex.vue';
import Button from '@/components/Button/Button.vue';
import LogoutIcon from '@/components/Icons/Logout.vue';
import { useI18n } from 'vue-i18n';
import { onBeforeUnmount, onMounted, ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import Logo from '@/components/Logo/Logo.vue';

const auth = useAuth();
const { t } = useI18n();
const route = useRoute();
const menuOpen = ref(false);
const sidebar = ref<HTMLElement | null>(null);
const hamburger = ref<HTMLElement | null>(null);

const isActive = (path: string) => computed(() => route.path.startsWith(path));

const handleClickOutside = (event: MouseEvent) => {
  if (
    menuOpen.value &&
    sidebar.value &&
    !sidebar.value.contains(event.target as Node)
  ) {
    if (hamburger.value && !hamburger.value.contains(event.target as Node)) {
      menuOpen.value = false;
    }
  }
};

const logout = () => {
  auth.logout();
};

onMounted(() => {
  document.addEventListener('logout', logout);
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('logout', logout);
  document.removeEventListener('click', handleClickOutside);
});
</script>
<style lang="scss" scoped>
.bold {
  font-weight: 500;
}

.header {
  margin-bottom: var(--md-spacing-40);
}

.layout-container {
  display: flex;
  gap: 2rem;
}

.sidebar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.3rem;
  text-decoration: none;
  color: var(--md-sys-color-on-surface);
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--md-sys-color-surface-variant);
  }

  &.router-link-active,
  &.router-link-exact-active,
  &.active {
    background-color: var(--md-sys-color-primary-container);
    color: var(--md-sys-color-on-primary-container);
    font-weight: 600;

    .material-icons {
      font-weight: 600;
    }
  }
}

.logout-wrapper {
  margin-top: auto;
  padding: 0.75rem 1rem;
}

.content {
  flex: 1;
  max-width: 800px;
  overflow: hidden;
}

.hamburger {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: var(--md-sys-color-on-surface);

  .material-icons {
    font-size: 24px;
  }
}

.overlay {
  display: none;
}

.desktop-only {
  display: flex;
}

.mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .header > div > div:first-child {
    width: 100%;
    position: relative;
    justify-content: center !important;
  }

  .hamburger {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
  }

  .phone-info {
    position: absolute;
    right: 0;
    align-items: flex-end;
    top: 50%;
    transform: translateY(-50%);
  }

  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: flex;
  }
  .hamburger {
    display: block;
  }

  .overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }

  .sidebar {
    position: fixed;
    left: -250px;
    top: 0;
    bottom: 0;
    width: 250px;
    background: var(--md-sys-color-surface);
    z-index: 1000;
    transition: left 0.3s;
    padding: 1rem;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);

    &.open {
      left: 0;
    }

    .logout-wrapper {
      border-top: 1px solid #e0e0e0;
      padding-top: 1rem;
      margin-left: -1rem;
      margin-right: -1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      display: flex;
      justify-content: center;
    }
  }

  .layout-container {
    flex-direction: column;
  }
}
</style>
